import type { IFiabilityStudy } from '@/shared/model/fiability-study.model';
import type { IFiabilityGroup } from '@/shared/model/fiability-grouping.model';
import axios from 'axios';

const apiUrl = 'api/fiabilityStudies';

export interface IValidationError {
  ruleId: string;
  message: string;
  severity: 'BLOCKING' | 'WARNING' | 'INFO';
  field?: string;
}

export interface IValidationResult {
  /** Jackson serializes Java `isValid()` getter as `valid` */
  valid: boolean;
  errors: IValidationError[];
  warnings: IValidationError[];
}

export interface ICompatibilityCheckResult {
  compatibleStudy: IFiabilityStudy | null;
  isCompatible: boolean;
  reason: string;
}

export default class FiabilityStudyService {
  public create(fiabilityStudy: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${apiUrl}`, fiabilityStudy)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieve(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(apiUrl)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveByFamily(id: number): Promise<IFiabilityStudy> {
    return new Promise<IFiabilityStudy>((resolve, reject) => {
      axios
        .get(`${apiUrl}/byFamily/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public update(entity: IFiabilityStudy): Promise<IFiabilityStudy> {
    return new Promise<IFiabilityStudy>((resolve, reject) => {
      axios
        .put(`${apiUrl}/${entity.id}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public delete(entity: IFiabilityStudy): Promise<IFiabilityStudy> {
    return new Promise<IFiabilityStudy>((resolve, reject) => {
      axios
        .post(`${apiUrl}/delete/${entity.id}`)
        .then(res => {
          resolve(entity);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  // ========== Duplication Methods ==========

  /**
   * Duplicate an existing study.
   * Creates a copy with status DRAFT and title prefixed with "Copie de ".
   */
  public duplicate(id: number): Promise<IFiabilityStudy> {
    return axios.post(`${apiUrl}/${id}/duplicate`).then(res => res.data);
  }

  // ========== Validation Workflow Methods (Phase 2) ==========

  /**
   * Submit a study for validation
   * Transition: DRAFT → VALIDATION
   */
  public submitForValidation(id: number): Promise<IValidationResult> {
    return new Promise<IValidationResult>((resolve, reject) => {
      axios
        .post(`${apiUrl}/${id}/submit`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Validate a study (make it immutable)
   * Transition: VALIDATION → VALIDATED
   */
  public validateStudy(id: number): Promise<IFiabilityStudy> {
    return new Promise<IFiabilityStudy>((resolve, reject) => {
      axios
        .post(`${apiUrl}/${id}/validate`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Reject a study back to draft
   * Transition: VALIDATION → DRAFT
   */
  public rejectToDraft(id: number): Promise<IFiabilityStudy> {
    return new Promise<IFiabilityStudy>((resolve, reject) => {
      axios
        .post(`${apiUrl}/${id}/reject`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  // ========== Compatibility Check Methods (Phase 4) ==========

  /**
   * Retrieve all VALIDATED studies for a given equipment family, ordered by end date descending.
   * Used by the aggregation picker dialog when creating an AUTOMATIC study.
   */
  public retrieveValidatedByFamily(familyId: number): Promise<IFiabilityStudy[]> {
    return new Promise<IFiabilityStudy[]>((resolve, reject) => {
      axios
        .get(`${apiUrl}/validatedByFamily/${familyId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  // ========== Groupings Methods (Phase 5) ==========

  /**
   * Get groupings from a study
   * Used for pre-filling when aggregating with a previous study
   */
  public getGroupings(studyId: number): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      axios
        .get(`${apiUrl}/${studyId}/groupings`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  // ========== Study Fetch Methods ==========

  /** Fetch a single study by ID. */
  public find(id: number): Promise<IFiabilityStudy> {
    return axios.get(`${apiUrl}/${id}`).then(res => res.data);
  }

  // ========== Homogeneity Preview Methods ==========

  /** Compute fdSum from Rex data for each grouping combination (preview at step 4). */
  public computeFdSums(request: IComputeFdSumsRequest): Promise<number[]> {
    return axios.post(`${apiUrl}/computeFdSums`, request).then(res => res.data);
  }

  /** Compute error factor (FE) from N (sum of degradation factors) using the chi-squared formula. */
  public computeErrorFactor(n: number): Promise<number> {
    return axios.get(`${apiUrl}/computeErrorFactor`, { params: { n } }).then(res => res.data);
  }

  /** Run pairwise homogeneity tests (preview at step 4). */
  public runPairwiseHomogeneityTests(pairs: IPairwiseTestInput[]): Promise<IHomogeneityTestDetailResult[]> {
    return axios.post(`${apiUrl}/pairwiseHomogeneityTests`, { pairs }).then(res => res.data);
  }

  // ========== Homogeneity Matrix Methods ==========

  /** Compute the design-pairwise homogeneity matrix (step 4 preview). */
  public computeHomogeneityMatrix(request: IHomogeneityMatrixRequest): Promise<IHomogeneityMatrixResponse> {
    return axios.post<IHomogeneityMatrixResponse>(`${apiUrl}/homogeneityMatrix`, request).then(res => res.data);
  }

  /** Compute suggested groupings using the C_paire algorithm (step 4 preview). */
  public computeSuggestedGroupings(request: IHomogeneityMatrixRequest): Promise<ISuggestedGroupingsResponse> {
    return axios.post<ISuggestedGroupingsResponse>(`${apiUrl}/suggestedGroupings`, request).then(res => res.data);
  }

  // ========== Aggregation Methods (Phase 6) ==========

  /**
   * Perform aggregation of a study with a previous study
   */
  public performAggregation(studyId: number, previousStudyId: number, forceAggregation: boolean = false): Promise<IAggregationResponse> {
    return new Promise<IAggregationResponse>((resolve, reject) => {
      axios
        .post(`${apiUrl}/${studyId}/aggregate`, {
          previousStudyId: previousStudyId,
          forceAggregation: forceAggregation,
        })
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
}

// ========== Aggregation DTOs (Phase 6) ==========

export interface IHomogeneityTestResult {
  passed: boolean;
  reasons: string[];
  grouping?: any[][];
  failureMode?: string;
}

export interface IAggregationResponse {
  success: boolean;
  study: any;
  testResult: IHomogeneityTestResult | null;
}

// ========== Homogeneity Preview DTOs ==========

export interface IComputeFdSumsRequest {
  equipmentFamilyId: number;
  start: Date | string;
  end: Date | string;
  groups: IFiabilityGroup[];
  dataItems: { grouping: string[][]; failureMode: string }[];
}

export interface IPairwiseTestInput {
  grouping: string[][];
  failureMode: string;
  previousFdSum: number;
  previousOperatingData: number;
  newFdSum: number;
  newOperatingData: number;
}

export interface ITestDetail {
  testName: string;
  passed: boolean;
}

export interface IHomogeneityTestDetailResult {
  passed: boolean;
  testDetails: ITestDetail[];
  reasons?: string[];
  grouping: string[][];
  failureMode: string;
}

// ========== Homogeneity Matrix DTOs ==========

export interface IDesignOperatingData {
  failureMode: string;
  projectDesign: string;
  operatingData: number;
}

export interface IHomogeneityMatrixRequest {
  equipmentFamilyId: number;
  start: Date | string;
  end: Date | string;
  operatingData: IDesignOperatingData[];
}

export interface IHomogeneityMatrixEntry {
  failureMode: string;
  design1: string;
  design2: string;
  passed: boolean;
  testDetails: ITestDetail[];
}

export interface IHomogeneityMatrixResponse {
  entries: IHomogeneityMatrixEntry[];
}

/** Response from the C_paire suggested groupings endpoint. Keyed by failure mode. */
export interface ISuggestedGroupingsResponse {
  [failureMode: string]: ISuggestedGroupingResult;
}

export interface ISuggestedGroupingResult {
  suggestedGroupings: string[][];
  pairwiseResults: ISuggestedPairResult[];
}

export interface ISuggestedPairResult {
  group1: string;
  group2: string;
  homogeneous: boolean;
  applicable: boolean;
  testResults: Record<string, boolean>;
}
